import { focusTrap } from '@primer/behaviors';
import {
  toggleAttribute,
  toggleExpanded,
  toggleHidden,
  toggleHiddenUntilFound,
} from 'src/utils/dom-toggle';
import { Context, Controller } from '@hotwired/stimulus';

export default class SidebarController extends Controller {
  public static targets = [
    'mobileWrapper',
    'mobileContentSlot',
    'content',
    'wrapper',
    'overlay',
    'sidebar',
    'trigger',
    'actions',
    'iconExpanded',
    'iconCollapsed',
  ];

  declare private mobileWrapperTarget: HTMLTemplateElement;
  declare private mobileContentSlotTarget: HTMLElement;
  declare private contentTarget: HTMLElement;
  declare private wrapperTarget: HTMLElement;
  declare private overlayTarget: HTMLElement;
  declare private sidebarTarget: HTMLElement;
  declare private triggerTarget: HTMLElement;
  declare private actionsTarget: HTMLElement;
  declare private iconExpandedTarget: HTMLElement;
  declare private iconCollapsedTarget: HTMLElement;

  private focusController: AbortController | undefined;
  private cleanupTimeout: number | undefined;
  private mobileWrapper: Element | undefined;
  private isMobileActive: boolean = false;
  private isMobileMediaQueryList: MediaQueryList | undefined;
  private contentContainer: HTMLElement | undefined;

  public constructor(context: Context) {
    super(context);

    this.onBeforeMatchContent = this.onBeforeMatchContent.bind(this);
    this.onMobileActiveChange = this.onMobileActiveChange.bind(this);
  }

  public connect(): void {
    this.mobileWrapper = document.importNode(
      this.mobileWrapperTarget.content,
      true,
    ).firstElementChild!;

    toggleHidden(this.mobileWrapper, true);
    this.element.appendChild(this.mobileWrapper);

    // Make sure it's closed
    // toggleExpanded(this.triggerTarget, false);
    // toggleHidden(this.wrapperTarget, true);

    // Make sure animations are set-up
    this.overlayTarget.classList.add('opacity-0');
    this.overlayTarget.classList.remove('opacity-100');
    this.sidebarTarget.classList.add('-translate-x-full');
    this.sidebarTarget.classList.remove('translate-x-0');
    this.actionsTarget.classList.add('opacity-0');
    this.actionsTarget.classList.remove('opacity-100');
    toggleAttribute('disabled', this.triggerTarget, false);

    // Clone menu into mobile wrapper
    this.mobileContentSlotTarget.append(
      (this.contentTarget.cloneNode(true) as HTMLElement).firstElementChild!,
    );

    this.contentContainer =
      document.querySelector('.peer\\/content') ?? undefined;

    const maxSize = getComputedStyle(this.contentTarget).getPropertyValue(
      '--breakpoint-md',
    );

    this.isMobileMediaQueryList = matchMedia(`(width >= ${maxSize})`);
    this.isMobileActive = !this.isMobileMediaQueryList.matches;

    try {
      this.opened =
        localStorage.getItem('sidebar') !== 'closed' && !this.isMobileActive;
    } catch (_) {
      this.opened = !this.isMobileActive;
    }

    // Setup listeners
    this.contentTarget.addEventListener(
      'beforematch',
      this.onBeforeMatchContent,
    );
    this.isMobileMediaQueryList.addEventListener(
      'change',
      this.onMobileActiveChange,
    );
  }

  public disconnect(): void {
    toggleExpanded(this.triggerTarget, false);
    toggleHidden(this.wrapperTarget, true);

    this.focusController?.abort('Sidebar is disconnected');
    clearTimeout(this.cleanupTimeout);

    // Remove listeners
    this.contentTarget.removeEventListener(
      'beforematch',
      this.onBeforeMatchContent,
    );

    this.isMobileMediaQueryList?.removeEventListener(
      'change',
      this.onMobileActiveChange,
    );

    this.mobileWrapper?.remove();
  }

  public get opened(): boolean {
    return this.triggerTarget.getAttribute('aria-expanded') === 'true';
  }

  public set opened(next: boolean) {
    try {
      localStorage.setItem('sidebar', next ? 'opened' : 'closed');
    } catch (_) {
      // swallow
    }

    toggleExpanded(this.triggerTarget, next);

    if (this.isMobileActive) {
      document.body.classList.toggle('overflow-hidden!', next);
      this.contentContainer?.classList.toggle('overflow-hidden!', next);

      if (next) {
        this.contentContainer?.classList.toggle('scroll-pt-[100px]', false);
      }
    }

    if (next) {
      toggleHidden(this.wrapperTarget, false);
      toggleHidden(this.iconCollapsedTarget, true);
      toggleHidden(this.iconExpandedTarget, false);
      toggleHiddenUntilFound(this.contentTarget, false);

      this.focusController = focusTrap(
        this.wrapperTarget,
        this.actionsTarget.querySelector('button') || undefined,
      );
    } else {
      if (this.isMobileActive) {
        const onCleanupCancel = () => {
          clearTimeout(this.cleanupTimeout);
          this.cleanupTimeout = undefined;

          this.sidebarTarget.removeEventListener(
            'transitioncancel',
            onCleanupCancel,
          );
        };

        const onVisuallyHidden = () => {
          onCleanupCancel();
          this.sidebarTarget.removeEventListener(
            'transitionend',
            onVisuallyHidden,
          );

          if (this.opened) {
            return;
          }

          toggleHidden(this.wrapperTarget, true);
          toggleHidden(this.iconCollapsedTarget, false);
          toggleHidden(this.iconExpandedTarget, true);
          toggleHiddenUntilFound(this.contentTarget, true);
        };

        this.sidebarTarget.addEventListener('transitionend', onVisuallyHidden);
        this.sidebarTarget.addEventListener(
          'transitioncancel',
          onCleanupCancel,
        );

        this.cleanupTimeout = setTimeout(() => {
          onVisuallyHidden();
        }, 5000) as unknown as number;
      } else {
        toggleHidden(this.wrapperTarget, true);
        toggleHidden(this.iconCollapsedTarget, false);
        toggleHidden(this.iconExpandedTarget, true);
        toggleHiddenUntilFound(this.contentTarget, true);
      }

      // Focus the menu button
      this.focusController?.abort('Sidebar is closed');
      this.triggerTarget.focus();

      this.contentContainer?.classList.toggle('scroll-pt-[100px]', true);
    }

    requestAnimationFrame(() => {
      if (!this.triggerTarget) {
        return;
      }

      this.overlayTarget.classList.toggle('opacity-0', !next);
      this.overlayTarget.classList.toggle('opacity-100', next);

      this.sidebarTarget.classList.toggle('-translate-x-full', !next);
      this.sidebarTarget.classList.toggle('translate-x-0', next);

      this.actionsTarget.classList.toggle('opacity-0', !next);
      this.actionsTarget.classList.toggle('opacity-100', next);
    });
  }

  public onOpen(): void {
    this.opened = true;
  }

  public onClose(): void {
    this.opened = false;
  }

  public onToggle(): void {
    this.opened = !this.opened;
  }

  private onMobileActiveChange(ev: MediaQueryListEvent): void {
    this.isMobileActive = !ev.matches;
    this.contentContainer?.classList.toggle(
      'scroll-pt-[100px]',
      !this.isMobileActive || !this.opened,
    );

    if (this.isMobileActive && this.opened) {
      document.body.classList.toggle('overflow-hidden!', true);
      this.contentContainer?.classList.toggle('overflow-hidden!', true);
    }

    if (!this.isMobileActive && !this.opened) {
      document.body.classList.toggle('overflow-hidden!', false);
      this.contentContainer?.classList.toggle('overflow-hidden!', false);
    }
  }

  public onBeforeMatchContent(event: Event): void {
    if (this.opened) {
      return;
    }

    this.triggerTarget.click();

    if (event.target instanceof HTMLElement) {
      event.target.scrollIntoView({ behavior: 'auto', block: 'nearest' });
    }
  }
}
